import React, { useEffect, useState } from 'react';

import { Formik, FormikValues } from 'formik';
import { ThemeProvider } from 'styled-components';
import * as yup from 'yup';

import BottomSheet from 'components/BottomSheet';
import Field from 'components/Field';
import ModalSelect from 'components/Selects/ModalSelect';
import SimpleAlert from 'components/SimpleAlert';
import TextArea from 'components/TextArea';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { BreakRulesReason, getBreakRulesReasonForTravel } from 'services/travelBreakRulesReasonService';
import { getReasonsForTravel, Reason } from 'services/travelReasonService';

import { customTheme } from './customTheme';
import {
  ConfirmButton,
  Container,
  ButtonContainer,
  Header,
  CloseButton,
  IconClose,
  FormContainer,
  Text,
  TagPolicy,
  Title,
  Spacer,
} from './styles';
import useCompanyConfiguration from 'hooks/useCompanyConfiguration';

interface Props {
  show: boolean;
  onClose: () => void;
  onConfirm: (values: ValueForm) => void;
  isLoading: boolean;
  breakRulesMessage?: string | string[];
  valueTotal: string;
}

export type ValueForm = {
  justifyForBreakingRules: string;
  reason?: string;
  breakRulesReason?: string;
};

const FlightsExchangeJustifyConfirm = ({
  show,
  onClose,
  onConfirm,
  isLoading,
  breakRulesMessage,
  valueTotal,
}: Props) => {
  const { t } = useStoreTranslation();
  const [reasons, setReasons] = useState<Reason[]>([]);
  const [breakRulesReasons, setBreakRulesReasons] = useState<BreakRulesReason[]>([]);
  const [selectedReason, setSelectedReason] = useState<string>();
  const [breakReason, setBreakReason] = useState<string>();
  const [loading, setLoading] = useState(false);
  const companyConfiguration = useCompanyConfiguration();
  
  useEffect(() => {
    setLoading(true);
    getReasonsForTravel().then((res) => {
      setReasons(res.data);
    });

    getBreakRulesReasonForTravel().then((res) => {
      setBreakRulesReasons(res.data);
    });
  }, []);

  useEffect(() => {
    if (loading && reasons && breakRulesReasons) {
      setLoading(false);
    }
  }, [breakRulesReasons, loading, reasons]);

  const isBreakRulesMessageValid = () => {
    if (breakRulesMessage && breakRulesMessage.length > 0 && breakRulesReasons && breakRulesReasons.length > 0) {
      if ((document.getElementById('custom_select_breakRulesReason') as HTMLInputElement)?.value === '') {
        return false;
      } else {
        return true;
      }
    }
    return true;
  };

  const initValues: ValueForm = {
    justifyForBreakingRules: '',
    reason: undefined,
    breakRulesReason: undefined,
  };

  const validationSchema = yup.object().shape({
    justifyForBreakingRules: companyConfiguration?.menuView?.hideTravelReason ? yup.string().min(10).max(300).trim() : yup.string().min(10).max(300).trim().required(),
    breakRulesReason: yup.string().required(),
    reason: yup.string().required(),
  });

  return (
    <ThemeProvider theme={customTheme}>
      <BottomSheet
        noHeader
        modal
        show={show}
        onClose={onClose}
        style={{ maxHeight: 710, backgroundColor: '#F9F9F9' }}
        zIndex={17}
      >
        <Header>
          <Header.Title>{t('flightsExchange.justify.header')}</Header.Title>
          <Header.Close>
            <CloseButton onClick={onClose}>
              <IconClose />
            </CloseButton>
          </Header.Close>
        </Header>
        {loading ? (
          <Container>Loading</Container>
        ) : (
          <Container>
            <Formik
              onSubmit={(data) => {
                onConfirm(data);
              }}
              initialValues={initValues}
              validationSchema={validationSchema}
              isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
            >
              {({ isValid }: FormikValues) => (
                <FormContainer>
                  <Text>
                    <TagPolicy>
                      <TagPolicy.Icon />
                      {t('requestCard.details.policyStatus.invalid')}
                    </TagPolicy>
                  </Text>
                  <Text>{t('flightsExchange.justify.text')}</Text>
                  <Field name="reason">
                    <ModalSelect
                      value={selectedReason}
                      onChange={(value) => setSelectedReason(value)}
                      options={reasons}
                      title={t('flightsExchange.justify.form.reason')}
                      testId="modal-search-testId"
                      zIndex={20}
                      name="input_reason"
                    />
                  </Field>

                  <Field name="justifyForBreakingRules">
                    <TextArea
                      data-testid="travel-justify-textarea"
                      placeholder={t('flightsExchange.justify.form.justifyForBreakingRules')}
                    />
                  </Field>
                  <Spacer />

                  {breakRulesMessage &&
                    breakRulesMessage.length > 0 &&
                    breakRulesReasons &&
                    breakRulesReasons.length > 0 && (
                      <>
                        {breakRulesMessage.length > 0 && typeof breakRulesMessage !== 'string' && (
                          <SimpleAlert
                            title={t('travelJustify.breakRulesReason.outOfPolicies')}
                            content={
                              <ul>
                                {(breakRulesMessage as string[])?.map((msg) => (
                                  <li key={msg}>&bull; {msg}</li>
                                ))}
                              </ul>
                            }
                          />
                        )}
                        {typeof breakRulesMessage === 'string' && (
                          <SimpleAlert
                            title={t('travelJustify.breakRulesReason.outOfPolicies')}
                            content={
                              <ul>
                                <li key="breakRulesMessage1">&bull; {breakRulesMessage}</li>
                              </ul>
                            }
                          />
                        )}
                        <Field name="breakRulesReason">
                          <ModalSelect
                            value={breakReason}
                            onChange={(value) => setBreakReason(value)}
                            options={breakRulesReasons}
                            title={t('flightsExchange.justify.form.breakRulesReasons')}
                            testId="modal-justify-testId"
                            zIndex={20}
                            name="input_breakRulesReason"
                          />
                        </Field>
                      </>
                    )}
                  <Title>{t('flightsExchange.justify.confirm')}</Title>
                  <ButtonContainer>
                    <ConfirmButton
                      type="submit"
                      disabled={!isValid || isLoading || !isBreakRulesMessageValid()}
                      loading={isLoading}
                      testId="travel-justify-submit-button"
                    >
                      <ConfirmButton.Content>
                        <ConfirmButton.Label>{t('flightsExchange.justify.submit')}</ConfirmButton.Label>
                        <ConfirmButton.Price>
                          <ConfirmButton.Total>{t('flightsExchange.justify.total')}:</ConfirmButton.Total>
                          <ConfirmButton.Value>{valueTotal}</ConfirmButton.Value>
                        </ConfirmButton.Price>
                      </ConfirmButton.Content>
                    </ConfirmButton>
                  </ButtonContainer>
                </FormContainer>
              )}
            </Formik>
          </Container>
        )}
      </BottomSheet>
    </ThemeProvider>
  );
};

export default FlightsExchangeJustifyConfirm;
